import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink, Facebook, Sun, Moon, Search, Lock, ChevronDown, CornerDownRight, Languages } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { SearchDialog } from './SearchDialog';

// Navigation links with translation keys
const getNavLinks = (t) => [
  { name: t('nav.home'), path: '/' },
  { name: t('nav.about'), path: '/about' },
  { name: t('nav.football'), path: '/soccer' },
  { name: t('nav.matches'), path: '/kamper' },
  { name: t('nav.tennis'), path: '/tennis' },
  { name: t('nav.dance'), path: '/dance-pilates' },
  { name: t('nav.runClub', 'Social Run Club'), path: '/social-run-club' },
  { name: t('nav.rentals'), path: '/rentals' },
  { name: t('nav.pricing'), path: '/pricing', children: [
    { name: t('nav.fritidskortet', 'Fritidskortet'), path: '/fritidskortet' },
  ] },
  { name: t('nav.board'), path: '/styret' },
  { name: t('nav.contact'), path: '/contact' },
];

export const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme, isDark } = useTheme();
  const { t, i18n } = useTranslation();
  
  const navLinks = getNavLinks(t);
  const lang = (i18n.language || 'no').split('-')[0];
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAiTranslated = !isAdminRoute && (lang === 'so' || lang === 'ar');
  const [noticeDismissed, setNoticeDismissed] = useState(() => sessionStorage.getItem('aiNoticeDismissed') === '1');
  useEffect(() => { if (noticeDismissed) sessionStorage.setItem('aiNoticeDismissed', '1'); }, [noticeDismissed]);

  useEffect(() => {
    document.documentElement.lang = isAdminRoute ? 'no' : lang;
    document.documentElement.dir = lang === 'ar' && !isAdminRoute ? 'rtl' : 'ltr';
  }, [lang, isAdminRoute]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_active-club-1/artifacts/h8quxnq7_VSK%20logo_1.png" 
                alt="Veitvet Sportsklubb" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
              {navLinks.map((link) => (
                <div key={link.path} className="relative group">
                  <Link
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-0.5 px-2 xl:px-3 py-2 text-xs xl:text-sm font-medium uppercase tracking-wide transition-colors ${
                      location.pathname === link.path || link.children?.some((c) => c.path === location.pathname)
                        ? 'text-green-600'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {link.name}
                    {link.children && <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" aria-hidden="true" />}
                  </Link>
                  {link.children && (
                    <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-all duration-200">
                      <div className="min-w-[200px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-2">
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            data-testid={`nav-sub-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`block px-4 py-2 text-xs xl:text-sm font-medium uppercase tracking-wide transition-colors ${
                              location.pathname === child.path
                                ? 'text-green-600 bg-green-50 dark:bg-green-900/30'
                                : 'text-slate-600 dark:text-slate-300 hover:text-green-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center gap-1 xl:gap-2">
              {/* Search Button */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                data-testid="search-toggle-btn"
                aria-label={t('search.open', 'Søk på siden')}
                title={t('search.open', 'Søk (Ctrl+K)')}
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                data-testid="theme-toggle-btn"
                aria-label={isDark ? t('theme.light') : t('theme.dark')}
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* Følg oss - Facebook */}
              <a 
                href="https://www.facebook.com/share/17qxvdpLAN/"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:flex items-center gap-1 px-2 xl:px-3 py-1.5 text-xs xl:text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-green-600 transition-colors"
                data-testid="header-facebook-btn"
              >
                <Facebook className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
                <span className="hidden md:inline">{t('nav.followUs')}</span>
              </a>

              {/* Bli medlem button */}
              <a 
                href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:block"
                data-testid="header-join-btn"
              >
                <Button size="sm" className="bg-green-600 text-white text-xs xl:text-sm font-bold uppercase tracking-wide hover:bg-green-700 px-3 xl:px-4">
                  {t('nav.joinUs')}
                </Button>
              </a>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                ) : (
                  <Menu className="w-6 h-6 text-slate-700 dark:text-slate-300" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-700 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <nav className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <React.Fragment key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 text-sm font-medium uppercase tracking-wider rounded-md transition-colors ${
                      location.pathname === link.path
                        ? 'text-green-600 bg-green-50 dark:bg-green-900/30'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.children?.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-sub-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex items-center gap-2 pl-8 pr-4 py-2.5 text-xs font-medium uppercase tracking-wider rounded-md transition-colors ${
                        location.pathname === child.path
                          ? 'text-green-600 bg-green-50 dark:bg-green-900/30'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <CornerDownRight className="w-3.5 h-3.5" aria-hidden="true" />
                      {child.name}
                    </Link>
                  ))}
                </React.Fragment>
              ))}
              
              {/* Theme toggle in mobile menu */}
              <button
                onClick={() => {
                  toggleTheme();
                }}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-medium uppercase tracking-wider rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {isDark ? t('theme.light') : t('theme.dark')}
              </button>
              
              {/* Language Switcher in mobile menu */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-2 mt-2">
                <LanguageSwitcher isMobile={true} />
              </div>
              
              <a 
                href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-3 text-sm font-medium uppercase tracking-wider rounded-md bg-green-600 text-white text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.joinUs')}
              </a>
              <a 
                href="https://www.facebook.com/share/17qxvdpLAN/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium uppercase tracking-wider rounded-md text-slate-600 dark:text-slate-300 hover:text-green-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Facebook className="w-4 h-4" />
                {t('nav.followUs')}
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="pt-16 md:pt-20">
        {children}
        {isAiTranslated && !noticeDismissed && (
          <div className="fixed bottom-0 inset-x-0 z-40 bg-amber-50 dark:bg-amber-900 border-t border-amber-200 dark:border-amber-700 shadow-lg" data-testid="ai-translation-notice">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 text-xs sm:text-sm text-amber-900 dark:text-amber-100 flex items-start gap-3">
              <Languages className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span className="flex-1">{t('common.aiTranslationNotice')}</span>
              <button type="button" onClick={() => setNoticeDismissed(true)} data-testid="ai-translation-notice-close" aria-label="OK" className="p-1 rounded hover:bg-amber-200/60 dark:hover:bg-amber-800">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Search Dialog */}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_active-club-1/artifacts/h8quxnq7_VSK%20logo_1.png" 
                  alt="Veitvet Sportsklubb" 
                  className="h-14 w-auto"
                />
              </div>
              <p className="text-slate-400 text-sm mb-4">
                {t('footer.description')}
              </p>
              <a 
                href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                {t('nav.joinUs')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-bold uppercase tracking-wider mb-4">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-slate-400 hover:text-white text-sm transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-bold uppercase tracking-wider mb-4">{t('footer.contact')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li className="font-medium text-white">Veitvet Sportsklubb</li>
                <li>Postboks 15, 0518 Oslo</li>
                <li>Org.nr: 971 528 117</li>
                <li className="pt-2">
                  <a href="mailto:nickolas.paynter@veitvet-sportsklubb.no" className="hover:text-green-400 transition-colors">
                    nickolas.paynter@veitvet-sportsklubb.no
                  </a>
                </li>
                <li>
                  <a href="tel:+4790739749" className="hover:text-green-400 transition-colors">
                    <bdi dir="ltr">+47 907 39 749</bdi>
                  </a>
                </li>
                <li className="text-xs text-slate-500">Man-Fre: 10:00 - 15:00</li>
              </ul>
            </div>

            {/* Hours & External Links */}
            <div>
              <h4 className="text-white font-bold uppercase tracking-wider mb-4">{t('footer.usefulLinks')}</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <a 
                    href="https://www.facebook.com/share/17qxvdpLAN/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    <Facebook className="w-4 h-4" /> {t('nav.followUs')}
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.hoopit.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    Hoopit <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://fiks.fotball.no/FiksWeb/Login?returnUrl=%2fFiksWeb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    FIKS <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.minidrett.no/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    Min Idrett <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://www.fotball.no/kretser/oslo/konkurranse/artikler/2026/endringer-breddereglementet-2026/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    Breddereglementet 2026 <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    {t('nav.joinUs')} <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://cometsport.no/products/index/185?type=category"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-green-400 transition-colors flex items-center gap-1"
                  >
                    {t('home.clubCollection')} <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-green-400 transition-colors">
                    {t('nav.contact')}
                  </Link>
                </li>
                <li>
                  <Link to="/rentals" className="hover:text-green-400 transition-colors">
                    {t('nav.rentals')}
                  </Link>
                </li>
                <li>
                  <Link to="/personvern" className="hover:text-green-400 transition-colors">
                    {t('footer.privacy')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-700 mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-slate-500 text-sm">
            <p>© {new Date().getFullYear()} Veitvet Sportsklubb. {t('footer.allRights')}</p>
            <div className="flex items-center gap-4">
              <Link to="/personvern" className="hover:text-green-400 transition-colors">
                {t('footer.privacyPolicy')}
              </Link>
              <span className="text-slate-700">·</span>
              <Link
                to="/admin/login"
                data-testid="footer-admin-link"
                className="inline-flex items-center gap-1.5 text-slate-500 hover:text-green-400 transition-colors"
                title="Innlogging for klubbens administratorer"
              >
                <Lock className="w-3.5 h-3.5" />
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
